import React, { useState, useEffect } from 'react';
import theme from '../../../../../../assets/styles/theme/theme';
import moment from 'moment';
import {
  CustomTableCell,
  TableContainer
} from '../../../../../../components/Commons/Tables/styles';
import {
  EOrderTab,
  IBilledOrderData
} from '../../../../../../store/ducks/long-steel-orders/types';
import {
  ECustomerTypeView,
  EUserPreferenceType,
  IUserOrderItemPreference,
  IUserPreference
} from '../../../../../../store/ducks/user/types';
import {
  formatCurrency,
  formatNumberTon
} from '../../../../../../utils/formatNumbers';
import ExpandedBilledOrderRowContent from './ExpandedBilledOrderRowContent';
import NFEColumn from './NFEColumn';
import QualityCertificationColumn from './QualityCertificationColumn';
import ShippingNoteColumn from './ShippingNoteColumn';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../store';
import MultipleDownloadHeader from './MultipleDownloadHeader';
import {
  ButtonsTableContent,
  Container,
  MultipleDownloadContainer
} from './styles';
import ExportOrders from '../../ExportOrders';
import ConfigTables from '../../ConfigTables';
import { Table, Pagination } from '@gerdau/hefesto-mais-web';
import { Tooltip } from '@gerdau/hefesto';
import {
  CompareFn,
  SorterResult,
  TableRowSelection
} from 'antd/lib/table/interface';
import CGQualityCertificateColumn from './CGQualityCertificateColumn';
import { GetFlag } from '../../../../../../utils/featureFlag';
import { TableProps } from 'antd';
import { PaginationProps } from '@gerdau/hefesto-mais-web/Pagination/Pagination.types';

interface IBilledOrdersTableProps {
  filteredOrders: IBilledOrderData[];
  userOrderPreference: IUserOrderItemPreference;
  customerViewType: ECustomerTypeView;
}

export interface IBilledOrdersTableDataSource {
  key: number;
  item: IBilledOrderData;
  // NOTA FISCAL
  invoiceNumber: string;
  // Nota de remessa   DOWNLOAD OBS: ordersMeta.canShowDownloadDocumentZvoiColumn
  deliveryDocumentZvoi: string;
  // emissão nota
  itemBillingDate: string;
  // Cód. material Gerdau
  sku: string;
  // OU
  // Cód. material cliente
  customerSKU: string;
  // Descrição do produto
  materialDescription: string;
  // Qtde faturada do item    OBS: verificar essa regra
  billingMeasureUnit: string;
  // Valor unitário
  unitPriceWithoutTaxes: string;
  // Valor total do item s/ impostos
  invoiceItemValueWithoutTaxes: string;
  // Valor total do item c/ impostos
  invoiceItemValueWithTaxes: string;
  // Certificado de qualidade
  qcNumber: string;
}

const BilledOrdersTable: React.FC<IBilledOrdersTableProps> = ({
  filteredOrders,
  userOrderPreference,
  customerViewType
}) => {
  const dispatch = useDispatch();
  const pageSizeOptions =
    window.innerWidth <= Number(theme.breakpoints.tablet.replace('px', ''))
      ? ['5', '10', '20']
      : ['20', '30', '50'];

  const [originalData, setOriginalData] = useState<
    IBilledOrdersTableDataSource[]
  >([]);
  const [dataSource, setDataSource] = useState<IBilledOrdersTableDataSource[]>(
    []
  );
  const [currentSorter, setCurrentSorter] = useState<
    | SorterResult<IBilledOrdersTableDataSource>
    | SorterResult<IBilledOrdersTableDataSource>[]
  >({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(11);

  const [summaryAlreadyUpdated, setSummaryAlreadyUpdated] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<
    IBilledOrdersTableDataSource[]
  >([]);

  const maxNumberOfSelectedItems = 20;

  const [isDownloadingMultipleItems, setIsDownloadingMultipleItems] =
    useState(false);

  const defaultOrderUserPreference: IUserPreference = {
    name: EUserPreferenceType.ORDERS,
    ordersItens: {
      idOrder: '0',
      idProduct: '0'
    }
  };

  const cgArray = ['BRCG', 'BRGO'];
  const qualityCertificateFlag = GetFlag('cg_certificado_qualidade');
  const orderUserPreference =
    useSelector(
      (state: ApplicationState) =>
        state.user.preferences.find(
          (pref) => pref.name === 'orders'
        ) as IUserPreference
    ) || defaultOrderUserPreference;

  const updateSummary = (page = 1, _pageSize = Number(pageSizeOptions[0])) => {
    const sliceItems = filteredOrders.slice(page, _pageSize);
    setCurrentPage(1);
    setOriginalData(
      convertFilteredOrdersToIBilledOrdersTableDataSource(filteredOrders)
    );
    setDataSource(
      convertFilteredOrdersToIBilledOrdersTableDataSource(sliceItems)
    );
    setSummaryAlreadyUpdated(true);
  };

  const convertFilteredOrdersToIBilledOrdersTableDataSource = (
    dataBilledOrders: IBilledOrderData[]
  ) => {
    return dataBilledOrders.map((item, idx) => {
      return {
        key: idx,
        item,
        invoiceNumber: item.invoiceNumber,
        deliveryDocumentZvoi: item.deliveryDocumentZvoi,
        itemBillingDate: moment(item.itemBillingDate, 'YYYY-MM-DD').format(
          'DD/MM/YYYY'
        ),

        sku: item?.billingHistory?.sku?.toString(),
        // OU
        customerSKU: item?.billingHistory?.customerSKU,

        materialDescription: item?.billingHistory?.materialDescription,

        billingMeasureUnit: `${formatNumberTon(
          item?.quantities?.billingQuantity?.toString()
        )} ${item?.quantities?.billingMeasureUnit}`,
        unitPriceWithoutTaxes: Number(item.conditions?.unitPriceWithoutTaxes)
          ? `R$ ${formatCurrency(item?.conditions?.unitPriceWithoutTaxes)}`
          : '-',

        invoiceItemValueWithoutTaxes: Number(
          item?.conditions?.invoiceItemValueWithoutTaxes
        )
          ? `R$ ${formatCurrency(
              item?.conditions?.invoiceItemValueWithoutTaxes
            )}`
          : '-',
        invoiceItemValueWithTaxes: Number(
          item?.conditions?.invoiceItemValueWithTaxes
        )
          ? `R$ ${formatCurrency(item?.conditions?.invoiceItemValueWithTaxes)}`
          : '-',

        qcNumber: item?.cqNumber
      };
    });
  };

  useEffect(() => {
    if (filteredOrders) {
      setSummaryAlreadyUpdated(false);
    }
  }, [dispatch, filteredOrders]);

  useEffect(() => {
    if (!summaryAlreadyUpdated) {
      updateSummary(currentPage - 1, pageSize);
      cleanSelectedRows();
    }
  });

  const cleanSelectedRows = (
    itemsToRemove?: IBilledOrdersTableDataSource[]
  ) => {
    if (itemsToRemove) {
      setSelectedRows(
        selectedRows.filter((sr) =>
          itemsToRemove.some((item) => item.key === sr.key)
        )
      );
      setSelectedRowKeys(
        selectedRowKeys.filter((rk) =>
          itemsToRemove.some((item) => item.key === rk)
        )
      );
    } else {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      removeErrorInAllRows();
    }
  };

  const getColumns = () => {
    const columns = [
      {
        title: 'Nota fiscal',
        dataIndex: 'invoiceNumber',
        width: '9rem',
        sorter: (
          a: IBilledOrdersTableDataSource,
          b: IBilledOrdersTableDataSource
        ) => Number(a.invoiceNumber) - Number(b.invoiceNumber),
        render: (
          invoiceNumber: string,
          record: IBilledOrdersTableDataSource
        ) => (
          <CustomTableCell>
            <NFEColumn item={record.item} invoiceNumber={invoiceNumber} />
          </CustomTableCell>
        )
      },
      {
        title: 'Nota de remessa',
        dataIndex: 'deliveryDocumentZvoi',
        width: '6rem',
        sorter: (
          a: IBilledOrdersTableDataSource,
          b: IBilledOrdersTableDataSource
        ) => Number(a.deliveryDocumentZvoi) - Number(b.deliveryDocumentZvoi),
        render: (
          _deliveryDocumentZvoi: string,
          record: IBilledOrdersTableDataSource
        ) => (
          <CustomTableCell>
            <ShippingNoteColumn item={record.item} />
          </CustomTableCell>
        )
      },
      {
        title: 'Emissão da nota',
        dataIndex: 'itemBillingDate',
        width: '7rem',
        sorter: (
          a: IBilledOrdersTableDataSource,
          b: IBilledOrdersTableDataSource
        ) => {
          if (a.itemBillingDate && b.itemBillingDate) {
            return (
              moment(a.itemBillingDate, 'DD/MM/YYYY').toDate().getTime() -
              moment(b.itemBillingDate, 'DD/MM/YYYY').toDate().getTime()
            );
          } else if (a.itemBillingDate && !b.itemBillingDate) {
            return -1;
          } else if (!a.itemBillingDate && b.itemBillingDate) {
            return 1;
          } else {
            return 0;
          }
        },
        render: (itemBillingDate: string) => (
          <CustomTableCell>{itemBillingDate}</CustomTableCell>
        )
      },
      {
        title: 'Material Gerdau',
        dataIndex: 'sku',
        width: '7rem',
        render: (sku: string) => <CustomTableCell>{sku}</CustomTableCell>
      },
      {
        title: 'Material cliente',
        dataIndex: 'customerSKU',
        width: '8rem',
        render: (customerSKU: string) => (
          <CustomTableCell>{customerSKU ?? ' - '}</CustomTableCell>
        )
      },
      {
        title: 'Descrição do produto',
        dataIndex: 'materialDescription',
        width: '9rem',
        render: (materialDescription: string) => (
          <CustomTableCell>{materialDescription}</CustomTableCell>
        )
      },
      {
        title: 'Quantidade faturada do item',
        dataIndex: 'billingMeasureUnit',
        width: '5rem',
        render: (billingMeasureUnit: string) => (
          <CustomTableCell>{billingMeasureUnit}</CustomTableCell>
        )
      },
      {
        title: 'Valor unitário',
        dataIndex: 'unitPriceWithoutTaxes',
        width: '9rem',
        render: (unitPriceWithoutTaxes: string) => (
          <CustomTableCell>{unitPriceWithoutTaxes}</CustomTableCell>
        )
      },
      {
        title: 'Valor do item sem impostos',
        dataIndex: 'invoiceItemValueWithoutTaxes',
        width: '9rem',
        render: (invoiceItemValueWithoutTaxes: string) => (
          <CustomTableCell>{invoiceItemValueWithoutTaxes}</CustomTableCell>
        )
      },
      {
        title: 'Valor do item com impostos',
        dataIndex: 'invoiceItemValueWithTaxes',
        width: '9rem',
        render: (invoiceItemValueWithTaxes: string) => (
          <CustomTableCell>{invoiceItemValueWithTaxes}</CustomTableCell>
        )
      },
      {
        title: 'Certificado de qualidade',
        dataIndex: 'qcNumber',
        width: '8rem',
        render: (_qcNumber: string, record: IBilledOrdersTableDataSource) => (
          <CustomTableCell>
            {qualityCertificateFlag &&
            cgArray.includes(record.item.billingHistory.salesOrganization) ? (
              <CGQualityCertificateColumn item={record.item} />
            ) : (
              <QualityCertificationColumn item={record.item} />
            )}
          </CustomTableCell>
        )
      }
    ];

    const hideColumns: string[] = [];

    if (!filteredOrders.some((item) => item.deliveryDocumentZvoi)) {
      hideColumns.push('deliveryDocumentZvoi');
    }

    if (userOrderPreference.idProduct === '0') {
      // Material cliente
      hideColumns.push('customerSKU');
    } else {
      // Material Gerdau
      hideColumns.push('sku');
    }

    return columns.filter((col) => !hideColumns.includes(col.dataIndex));
  };

  const rowSelection: TableRowSelection<IBilledOrdersTableDataSource> = {
    selectedRowKeys,
    type: 'checkbox',
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
      removeErrorInAllRows();
    },
    getCheckboxProps: (record: IBilledOrdersTableDataSource) => {
      if (qualityCertificateFlag) {
        return {
          disabled:
            !record?.item?.qcState?.canDownloadQC ||
            isDownloadingMultipleItems ||
            cgArray.includes(record.item.billingHistory.salesOrganization)
        };
      }
      return {
        disabled:
          !record?.item?.qcState?.canDownloadQC || isDownloadingMultipleItems
      };
    },
    onSelectAll: () => {
      removeErrorInAllRows();
    },
    renderCell: (_value, record, _index, originNode) => {
      if (qualityCertificateFlag) {
        return cgArray.includes(
          record.item.billingHistory.salesOrganization
        ) ? (
          <Tooltip
            title="Esse item não pode ser selecionado para download em massa."
            placement="right"
          >
            <div>{originNode}</div>
          </Tooltip>
        ) : (
          <div>{originNode}</div>
        );
      }
      return <div>{originNode}</div>;
    }
  };

  const setErrorInRow = (
    item: IBilledOrdersTableDataSource,
    scrollToRow = false
  ) => {
    const setError = () => {
      const erroRowTds = document.querySelectorAll(
        `#billed-orders-table table > tbody > tr[data-row-key="${item.key}"] > td`
      );
      erroRowTds.forEach((td) => {
        td.classList.add('error-row');
        if (scrollToRow) {
          setTimeout(() => {
            td.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 100);
        }
      });
    };

    if (scrollToRow) {
      // vai para a página do item com erro
      const tdPage = Math.ceil((item.key + 1) / pageSize);
      setCurrentPage(tdPage);
      updateSummary(tdPage, pageSize);
      setTimeout(() => {
        setError();
      }, 100);
    } else {
      setError();
    }
  };

  const removeErrorInAllRows = () => {
    const erroRowTds = document.querySelectorAll('td.error-row');
    erroRowTds.forEach((td) => td.classList.remove('error-row'));
  };

  const sorterHelper = (
    sorter:
      | SorterResult<IBilledOrdersTableDataSource>
      | SorterResult<IBilledOrdersTableDataSource>[]
  ) => {
    let itemHelper: IBilledOrdersTableDataSource[] = [];
    if (!Array.isArray(sorter)) {
      if (sorter.order === 'ascend' && sorter.column) {
        itemHelper = originalData
          .concat()
          .sort(
            sorter.column.sorter as CompareFn<IBilledOrdersTableDataSource>
          );
      } else if (sorter.order === 'descend' && sorter.column) {
        itemHelper = originalData
          .concat()
          .sort(sorter.column.sorter as CompareFn<IBilledOrdersTableDataSource>)
          .reverse();
      } else {
        itemHelper = originalData;
      }
    }

    return itemHelper;
  };

  const handleTableSort: TableProps<IBilledOrdersTableDataSource>['onChange'] =
    (_pagination, _filters, sorter) => {
      const itemHelper = sorterHelper(sorter);

      const slicedItems = itemHelper.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      );
      setDataSource(slicedItems);
      setCurrentSorter(sorter);
    };

  const sortItems = (newPage?: number, newPageTotal?: number) => {
    const page = newPage ?? currentPage;
    const pageTotal = newPageTotal ?? pageSize;
    const itemHelper = sorterHelper(currentSorter);

    setDataSource(itemHelper.slice((page - 1) * pageTotal, page * pageTotal));
  };

  const handleChange = (current: number, pageSizeTable: number) => {
    setCurrentPage(current);
    setPageSize(pageSizeTable);
    sortItems(current, pageSizeTable);
  };

  return (
    <Container>
      {selectedRowKeys.length > 0 && (
        <MultipleDownloadContainer>
          <MultipleDownloadHeader
            selectedRows={selectedRows}
            cleanSelectedRows={cleanSelectedRows}
            setIsDownloadingMultipleItems={setIsDownloadingMultipleItems}
            setErrorInRow={(
              item: IBilledOrdersTableDataSource,
              scrollToRow?: boolean
            ) => setErrorInRow(item, scrollToRow)}
            maxNumberOfSelectedItems={maxNumberOfSelectedItems}
          />
        </MultipleDownloadContainer>
      )}

      <TableContainer>
        {filteredOrders.length > 0 && (
          <Container>
            <ButtonsTableContent>
              <ExportOrders
                orders={filteredOrders}
                orderTab={EOrderTab.BILLED}
              />
              {orderUserPreference && (
                <ConfigTables
                  orderUserPreference={orderUserPreference}
                  orderTab={EOrderTab.BILLED}
                />
              )}
            </ButtonsTableContent>
          </Container>
        )}
        <Table
          columns={getColumns()}
          dataSource={dataSource}
          onChange={handleTableSort}
          expandedRowRender={(record) => {
            return (
              <ExpandedBilledOrderRowContent
                item={record.item}
                userOrderPreference={userOrderPreference}
                customerViewType={customerViewType}
              />
            );
          }}
          rowSelection={rowSelection}
        />
        <Pagination
          total={filteredOrders.length}
          onChange={handleChange}
          showTotal={true}
        />
      </TableContainer>
    </Container>
  );
};

export default BilledOrdersTable;
